/**
 * Utility to add Credit Score fields to persisted Client object fields
 *
 * Run this once to migrate existing localStorage data to include the new fields.
 * This can be called from the browser console or as part of an initialization script.
 */

import { persistedObjectFields, type CustomField } from '../services/queries/fieldQueries';

/**
 * Add Credit Score fields to Client object if they don't already exist
 */
export function addCreditScoreFieldsToClient() {
  // Get current fields from localStorage
  const existingFields = persistedObjectFields.get();

  // Check if credit score fields already exist
  const hasInitialScore = existingFields.some((f: CustomField) =>
    f.objectId === 'obj_client' && f.fieldName === 'initialScore'
  );

  if (hasInitialScore) {
    console.log('✅ Credit Score fields already exist');
    return;
  }

  console.log('📝 Adding Credit Score fields to Client object...');

  // Get the highest field ID number for obj_client fields
  const clientFields = existingFields.filter((f: CustomField) => f.objectId === 'obj_client');
  const maxIdNumber = Math.max(
    ...clientFields.map((f: CustomField) => {
      const match = f.id.match(/field_obj_client_(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    }),
    51
  );

  // Define the new Credit Score fields
  const creditScoreFields: CustomField[] = [
    {
      id: `field_obj_client_${String(maxIdNumber + 1).padStart(3, '0')}`,
      objectId: 'obj_client',
      fieldName: 'initialScore',
      fieldLabel: 'Initial Credit Score',
      fieldType: 'number',
      description: 'Credit score at enrollment',
      required: false,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: ['admin', 'user']
      },
      createdBy: 'System',
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString()
    },
    {
      id: `field_obj_client_${String(maxIdNumber + 2).padStart(3, '0')}`,
      objectId: 'obj_client',
      fieldName: 'currentScore',
      fieldLabel: 'Current Credit Score',
      fieldType: 'number',
      description: 'Most recent credit score',
      required: false,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: ['admin', 'user']
      },
      createdBy: 'System',
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString()
    },
    {
      id: `field_obj_client_${String(maxIdNumber + 3).padStart(3, '0')}`,
      objectId: 'obj_client',
      fieldName: 'targetScore',
      fieldLabel: 'Target Credit Score',
      fieldType: 'number',
      description: 'Goal credit score',
      required: false,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: ['admin', 'user']
      },
      createdBy: 'System',
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString()
    },
    {
      id: `field_obj_client_${String(maxIdNumber + 4).padStart(3, '0')}`,
      objectId: 'obj_client',
      fieldName: 'scoreImprovement',
      fieldLabel: 'Score Improvement',
      fieldType: 'number',
      description: 'Points improved from initial score',
      required: false,
      unique: false,
      isPII: false,
      isStandard: true,
      isActive: true,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: ['admin', 'user']
      },
      createdBy: 'System',
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString()
    }
  ];

  // Add the new fields to localStorage using add() method
  creditScoreFields.forEach(field => {
    persistedObjectFields.add(field);
  });

  console.log('✅ Added 4 Credit Score fields to Client object');
  console.log('   - initialScore');
  console.log('   - currentScore');
  console.log('   - targetScore');
  console.log('   - scoreImprovement');
  console.log('🔄 Please refresh the page to see the changes');

  return creditScoreFields;
}

// Export for easy console access
if (typeof window !== 'undefined') {
  (window as any).addCreditScoreFields = addCreditScoreFieldsToClient;
}
